import React from "react";
import { GitBranch } from "lucide-react";

export interface HierarchyItem {
  key: string;
  label: string;
  altKeys?: string[];
}

export const DEFAULT_HIERARCHY_PATH: HierarchyItem[] = [
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "city_county", label: "City/County", altKeys: ["city", "county"] },
  { key: "zone", label: "Zone" },
  { key: "division", label: "Division" },
  { key: "area", label: "Area" },
  { key: "sub_area", label: "Sub-Area", altKeys: ["subArea", "sub_areas"] },
  { key: "premise", label: "Premises", altKeys: ["premises"] },
  { key: "unit", label: "Unit" },
];

interface HierarchyCardProps {
  currentLevelKey?: string;
  currentEntityName: string;
  childData?: any[];
  childConfig?: {
    childType?: string;
    childEntityTypeVal?: string;
    childLabelPlural?: string;
  };
  onChildClick?: (childItem: any) => void;
  hierarchyPath?: HierarchyItem[];
}

export const HierarchyCard: React.FC<HierarchyCardProps> = ({
  currentLevelKey = "unit",
  currentEntityName,
  childData,
  childConfig,
  onChildClick,
  hierarchyPath = DEFAULT_HIERARCHY_PATH,
}) => {
  const currentHierarchyIndex = Math.max(
    0,
    hierarchyPath.findIndex(
      (item) =>
        item.key === currentLevelKey ||
        (item.altKeys && item.altKeys.includes(currentLevelKey)),
    ),
  );

  const ancestorLevels = hierarchyPath.slice(0, currentHierarchyIndex);

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 p-4 shadow-xs space-y-3">
      <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-1.5">
        <GitBranch className="w-4 h-4 text-gray-500" />
        Hierarchy
      </h3>
      <div className="space-y-1 pt-1 text-xs max-h-80 overflow-y-auto pr-1">
        {/* Ancestor levels above current */}
        {ancestorLevels.map((item, idx) => (
          <div
            key={item.key}
            style={{ paddingLeft: `${idx * 14}px` }}
            className="flex items-center gap-2 py-1 text-gray-500 text-xs"
          >
            <span className="w-2.5 h-2.5 rounded-full border border-gray-400 shrink-0" />
            <span>{item.label}</span>
          </div>
        ))}

        {/* Current active level */}
        <div
          style={{
            marginLeft: `${currentHierarchyIndex * 14}px`,
          }}
          className="flex items-center justify-between p-2 rounded-lg bg-blue-50/90 text-blue-700 font-semibold border border-blue-100/80 my-1"
        >
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
            <span>{currentEntityName}</span>
          </span>
          <span className="text-[11px] bg-blue-100/80 text-blue-600 px-2 py-0.5 rounded font-normal">
            current
          </span>
        </div>

        {/* Child level items if present */}
        {childConfig?.childType && childData && childData.length > 0 && (
          <div
            style={{
              paddingLeft: `${(currentHierarchyIndex + 1) * 14}px`,
            }}
            className="space-y-1 pt-0.5"
          >
            {childData.map((childItem: any, childIdx: number) => (
              <div
                key={childItem.code || childItem.id || childIdx}
                className="flex items-center gap-1.5 py-1 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                onClick={() => onChildClick?.(childItem)}
              >
                <span className="text-gray-300 font-mono select-none">
                  └
                </span>
                <span className="truncate">{childItem.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HierarchyCard;
